import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User,
  getIdToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  type ConfirmationResult,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

export { type ConfirmationResult, RecaptchaVerifier };

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

export const authService = {
  // Sign in with Google OAuth
  signInWithGoogle: async (): Promise<AuthUser> => {
    const result = await signInWithPopup(auth, googleProvider);
    return mapFirebaseUser(result.user);
  },

  // Sign out
  signOut: async (): Promise<void> => {
    await signOut(auth);
  },

  // Get current user
  getCurrentUser: (): AuthUser | null => {
    const user = auth.currentUser;
    return user ? mapFirebaseUser(user) : null;
  },

  // Get ID token for API calls
  getIdToken: async (): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user) return null;
    return getIdToken(user, true);
  },

  // Subscribe to auth state changes
  onAuthStateChange: (callback: (user: AuthUser | null) => void): (() => void) => {
    return onAuthStateChanged(auth, (firebaseUser) => {
      callback(firebaseUser ? mapFirebaseUser(firebaseUser) : null);
    });
  },
};

// Helper to map Firebase User to AuthUser
function mapFirebaseUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
  };
}

export default authService;

// ── Email authentication ──────────────────────────────────────────────────────

export async function signInWithEmail(email: string, password: string): Promise<void> {
  const result = await signInWithEmailAndPassword(auth, email, password);
  if (!result.user.emailVerified) {
    await signOut(auth);
    throw new Error(
      'Please verify your email address before signing in. Check your inbox for the verification link.',
    );
  }
}

export async function registerWithEmail(email: string, password: string): Promise<void> {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(result.user);
  await signOut(auth);
}

// ── Phone authentication ──────────────────────────────────────────────────────

export function setupRecaptcha(elementId: string): RecaptchaVerifier {
  return new RecaptchaVerifier(auth, elementId, { size: 'invisible' });
}

export function sendPhoneCode(
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier,
): Promise<ConfirmationResult> {
  return signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
}

export function verifyPhoneCode(
  confirmationResult: ConfirmationResult,
  code: string,
): Promise<unknown> {
  return confirmationResult.confirm(code);
}
